/* ==========================================================================
   Desktop Sticky Cart — coupon module (parallel build, same backends).

   Mirrors the existing flow:
     • Best coupon + coupon list  ← POST /offers/v3/personalize
     • Apply / remove a coupon    ← POST /cart/handle-cart  (checkoutDiscountCodeApplyV2)
     • Applied code persisted in localStorage 'discountCode' (+ 'handle-cart',
       'disc_price') so it interops with the existing flow.

   Totals: coupons are NOT native Shopify cart discounts, so cart.total_price does
   NOT include them. We derive the discount from the handle-cart response and
   override the bill's Coupon Discount + Total Amount + footer price in place.

   ⚠️ VERIFY AGAINST A LIVE handle-cart RESPONSE: the discount derivation below
   (cart.total_price − paymentDue) assumes paymentDue is in major units and is
   comparable to the Shopify subtotal. Confirm units / fee-inclusion with a real
   response and adjust deriveDiscountPaise() if needed. The UI degrades safely:
   if the discount can't be derived, the applied state still shows, totals aren't
   overridden.

   Exposes window.StickyCartCoupon.apply(scope, { onChange }).
   ========================================================================== */
(function () {
  'use strict';

  var OFFERS_URL = 'https://supertails-backend.el.r.appspot.com/offers/v3/personalize';
  var HANDLE_CART_URL = 'https://supertails-backend.el.r.appspot.com/cart/handle-cart';
  var appliedInSession = false; // a coupon was applied since the "view all coupons" overlay opened
  var listOpenedAt = 0;         // timestamp the overlay opened (for time_on_coupon_list_ms)

  function getLocation() {
    try {
      return JSON.parse(localStorage.getItem('userLocation')) || {};
    } catch (e) {
      return {};
    }
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }

  // In-stock cart lines only — OOS items must not affect coupon eligibility/discount.
  function inStock(items) {
    if (window.StickyCart && typeof window.StickyCart.filterInStock === 'function') {
      return window.StickyCart.filterInStock(items);
    }
    return items || [];
  }
  function inStockSubtotalPaise(cart) {
    return inStock(cart.items).reduce(function (s, it) {
      return s + (it.final_line_price != null ? it.final_line_price : (it.line_price || 0));
    }, 0);
  }

  function gid(type, id) {
    return 'gid://shopify/' + type + '/' + id;
  }

  // gid:// → bare numeric id (mirrors cart-script.liquid removeHiddenLineItems).
  function bareId(raw) {
    if (raw == null) return null;
    raw = String(raw);
    return raw.indexOf('/') > -1 ? raw.split('/').pop() : raw;
  }

  function changeVariant(variantId, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: String(variantId), quantity: quantity })
    }).then(function (r) { return r.json(); }).catch(function () {});
  }

  function addVariant(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: String(variantId), quantity: quantity || 1 }] })
    }).then(function (r) { return r.json(); }).catch(function () {});
  }

  /* ---- BXGY (buy-X-get-Y) — parity with cart-revamp.liquid 825-858 ------- */
  function readBxgy() {
    try { return JSON.parse(localStorage.getItem('bxgyData')); } catch (e) { return null; }
  }

  // Add the gift (Y) variant for a bxgy coupon and persist bxgyData. Skips the add
  // if the variant is already in the cart (avoids double-add on re-apply).
  function applyBxgy(code, coupon) {
    var v = coupon.bxgy && coupon.bxgy.variant_id;
    if (!v) return Promise.resolve();
    var data = {
      discountCodeBxgy: code,
      bxgyItem: v,
      bxgyCount: 1,
      bxgyMobText: coupon.saveAppText || '',
      bxgyDeskText: coupon.saveText || ''
    };
    try { localStorage.setItem('bxgyData', JSON.stringify(data)); } catch (e) {}
    return getCart().then(function (cart) {
      var has = (cart.items || []).some(function (i) { return String(i.variant_id) === String(v); });
      if (has) return;
      return addVariant(v, (coupon.bxgy && coupon.bxgy.quantity) || 1);
    });
  }

  // Remove a previously-added bxgy gift + clear bxgyData (coupon changed/removed).
  function removeStoredBxgy() {
    var data = readBxgy();
    try { localStorage.removeItem('bxgyData'); } catch (e) {}
    if (data && data.bxgyItem) return changeVariant(data.bxgyItem, 0);
    return Promise.resolve();
  }

  // Remove any handle-cart lines flagged hide_from_frontend (cart-revamp.liquid 1927-1937).
  function removeHiddenItems(result) {
    var nodes = result && result.checkoutDiscountCodeApplyV2 &&
      result.checkoutDiscountCodeApplyV2.checkout &&
      result.checkoutDiscountCodeApplyV2.checkout.lineItems &&
      result.checkoutDiscountCodeApplyV2.checkout.lineItems.nodes;
    if (!nodes || !nodes.length) return Promise.resolve();
    var ids = nodes.filter(function (n) { return n && n.hide_from_frontend === true; })
      .map(function (n) { return bareId((n.variant && n.variant.id) || n.id); })
      .filter(Boolean);
    if (!ids.length) return Promise.resolve();
    return Promise.all(ids.map(function (id) { return changeVariant(id, 0); }));
  }

  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }

  // Server-rendered inputs for the offers API, merged from two sources:
  //   • [data-st-offers-items]    — AOV + per-item sku/collections/tags, re-rendered
  //                                 by the section on every cart mutation (last cart).
  //                                 (NOT [data-st-offers] — that attribute is already
  //                                 used by the offer-strips banner container.)
  //   • [data-st-offers-customer] — session-stable customer + pet, emitted by the
  //                                 shell in the parent page context (the Section
  //                                 Rendering API doesn't reliably expose `customer`).
  function parseJsonEl(sel) {
    var el = document.querySelector(sel);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function readOffersData() {
    var out = { pet: {}, customer: {}, items: [], aov: 0 };
    var sec = parseJsonEl('[data-st-offers-items]');
    if (sec) {
      if (sec.items) out.items = sec.items;
      if (sec.aov != null) out.aov = sec.aov;
    }
    var cus = parseJsonEl('[data-st-offers-customer]');
    if (cus) {
      if (cus.pet) out.pet = cus.pet;
      if (cus.customer) out.customer = cus.customer;
    }
    return out;
  }

  // Mirrors getDeviceType() in cart-revamp.liquid.
  function getDeviceType(ua) {
    if (/Android/i.test(ua)) return 'Mobile (Android)';
    if (/iPhone|iPad|iPod/i.test(ua)) return 'Mobile (iOS)';
    if (/Windows Phone/i.test(ua)) return 'Mobile (Windows Phone)';
    if (/Windows NT/i.test(ua)) return 'Desktop (Windows)';
    if (/Macintosh/i.test(ua)) return 'Desktop (Mac)';
    if (/Linux/i.test(ua)) return 'Desktop (Linux)';
    return 'Unknown';
  }

  function daysSince(dateStr) {
    if (!dateStr) return null;
    var t = new Date(dateStr).getTime();
    if (isNaN(t)) return null;
    return Math.floor((Date.now() - t) / 86400000);
  }

  function formatINR(paise) {
    var rupees = Math.round((paise || 0) / 100);
    try {
      return '₹' + rupees.toLocaleString('en-IN');
    } catch (e) {
      return '₹' + rupees;
    }
  }

  /* ---- Offers (best coupon + list) ------------------------------------- */
  // Payload mirrors getCouponList() in cart-revamp.liquid exactly. Customer/pet/
  // per-item collections + tags are server-rendered into [data-st-offers-items]; the
  // rest (deviceType, pincode, AOV, daysSinceLastOrder) is resolved client-side.
  function fetchOffers() {
    var loc = getLocation();
    var data = readOffersData() || { pet: {}, customer: {}, items: [], aov: 0 };
    var items = data.items || [];
    var pet = data.pet || {};
    var cust = data.customer || {};

    var collections = [];
    items.forEach(function (i) {
      (i.collections || []).forEach(function (h) { collections.push(h); });
    });

    var body = {
      sku: items.map(function (i) { return i.sku; }),
      page_name: 'cart',
      quantity: items.map(function (i) { return String(i.quantity); }),
      pincode: loc.pincode || getCookie('cpin') || '',
      platform: 'web',
      deviceType: getDeviceType(navigator.userAgent),
      petName: pet.name != null ? pet.name : null,
      petBreed: pet.breed != null ? pet.breed : null,
      petType: pet.type != null ? pet.type : null,
      customerDetails: {
        order_count: cust.order_count != null ? cust.order_count : null,
        minLTV: cust.minLTV != null ? cust.minLTV : null,
        lastOrderDate: cust.lastOrderDate || '',
        daysSinceLastOrder: daysSince(cust.lastOrderDate),
        tags: cust.tags != null ? cust.tags : null
      },
      collections: collections,
      brands: items.map(function (i) { return i.vendor; }),
      AOV: data.aov || 0,
      line_items: items.map(function (i) {
        return {
          brands: i.vendor,
          price: i.price,
          productId: i.product_id,
          collections: i.collections || [],
          tags: i.tags || []
        };
      }),
      tags: items.reduce(function (acc, i) { return acc.concat(i.tags || []); }, []),
      couponCode: null,
      version: 4
    };
    return fetch(OFFERS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        // Same visibility logic as the existing cart (cart-revamp.liquid):
        // only status 200 + coupons flagged displayOnCart == 1, applied-first.
        if (!data || data.status !== 200 || !Array.isArray(data.coupons)) return [];
        var list = data.coupons.filter(function (c) { return c && c.displayOnCart == 1; });
        var applied = (localStorage.getItem('discountCode') || '').toLowerCase();
        list.forEach(function (c) {
          c.isApplied = (c.couponCode || '').toLowerCase() === applied;
        });
        list.sort(function (a, b) {
          if (a.isApplied && !b.isApplied) return -1;
          if (!a.isApplied && b.isApplied) return 1;
          return 0;
        });
        return list;
      })
      .catch(function () { return []; });
  }

  /* ---- Apply / remove --------------------------------------------------- */
  function applyCoupon(code, cart, email) {
    var cartId = localStorage.getItem('cartIdd');
    var body = {
      cartId: cartId ? gid('Cart', cartId) : '',
      coupon: code,
      // handle-cart expects the BARE numeric variant id (cart_items_array_temp in
      // the existing flow), NOT the gid:// form — otherwise the coupon is rejected.
      // Only in-stock lines are sent so the discount is computed on the payable cart.
      lineItems: inStock(cart.items).map(function (i) {
        return { variantId: String(i.variant_id), quantity: i.quantity };
      }),
      customerEmail: email || ''
    };
    return fetch(HANDLE_CART_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function deriveDiscountPaise(result, cart) {
    try {
      var checkout = result &&
        result.checkoutDiscountCodeApplyV2 &&
        result.checkoutDiscountCodeApplyV2.checkout;
      var due = checkout && checkout.paymentDue && checkout.paymentDue.amount;
      if (due == null) return null;
      var duePaise = Math.round(parseFloat(due) * 100);
      if (isNaN(duePaise)) return null;
      // Compare against the in-stock subtotal (paymentDue reflects in-stock lineItems).
      var subtotal = inStockSubtotalPaise(cart);
      var discount = subtotal - duePaise;
      if (discount < 0) discount = 0;
      if (discount > subtotal) discount = subtotal;
      return discount;
    } catch (e) {
      return null;
    }
  }

  function isApplicable(result) {
    if (!result || result.error) return false;
    if (result.isCouponApplicable === false) return false;
    var errs = result.checkoutDiscountCodeApplyV2 &&
      result.checkoutDiscountCodeApplyV2.userErrors;
    if (errs && errs.length) return false;
    return true;
  }

  // Best-effort denial reason for the Coupon Denied analytics event.
  function denyReason(result) {
    if (!result) return 'invalid';
    if (result.isCouponApplicable === false) return 'not_applicable';
    var errs = result.checkoutDiscountCodeApplyV2 && result.checkoutDiscountCodeApplyV2.userErrors;
    if (errs && errs.length) {
      var m = (errs[0].message || '').toLowerCase();
      if (m.indexOf('minimum') > -1 || m.indexOf('min ') > -1) return 'min_order_not_met';
      return errs[0].code || 'not_applicable';
    }
    return 'invalid';
  }

  /* ---- Coupon row ------------------------------------------------------- */
  // Only updates the Coupon Discount row. The Total Amount + footer price are
  // owned by the fees module (assets/sticky-cart-fees.js), which subtracts the
  // stored disc_price so coupon + fees stay consistent.
  function overrideTotals(scope, discountPaise) {
    if (discountPaise == null) return;
    var couponCell = scope.querySelector('[data-st-bill-coupon]');
    if (couponCell) {
      couponCell.innerHTML = '<span class="st-cart__bill-pos">' + (discountPaise > 0 ? '− ' : '') + formatINR(discountPaise) + '</span>';
    }
  }

  /* ---- Render inline + overlay ----------------------------------------- */
  // Coupon errors surface as the shared sticky-cart toast (same notice style as the
  // disabled-"+" message), not an inline error row.
  function couponToast(msg) {
    if (window.StickyCart && typeof window.StickyCart.toast === 'function') window.StickyCart.toast(msg);
  }

  function renderApplied(scope, code, discountPaise) {
    var best = scope.querySelector('[data-st-coupon-best]');
    var applied = scope.querySelector('[data-st-coupon-applied]');
    if (best) best.hidden = true;
    if (!applied) return;
    setText(applied, '[data-st-coupon-applied-code]', code);
    setText(
      applied,
      '[data-st-coupon-applied-save]',
      discountPaise != null ? 'Saved ' + formatINR(discountPaise) + ' on this order' : 'Coupon applied'
    );
    applied.hidden = false;
  }

  function renderBest(scope, best) {
    var bestEl = scope.querySelector('[data-st-coupon-best]');
    var applied = scope.querySelector('[data-st-coupon-applied]');
    if (applied) applied.hidden = true;
    if (!bestEl || !best) return;
    setText(bestEl, '[data-st-coupon-code]', (best.couponCode || '').trim());
    setHtml(bestEl, '[data-st-coupon-save]', best.saveText || '');
    bestEl.setAttribute('data-code', (best.couponCode || '').trim());
    bestEl.hidden = false;
    if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.bestCouponDisplayed === 'function') {
      window.StickyCartAnalytics.bestCouponDisplayed((best.couponCode || '').trim(), best.saveText || '', best.discountValue);
    }
  }

  // Notice shown when a previously-applied coupon was auto-invalidated (cart change / login).
  function renderNotice(scope, code) {
    var best = scope.querySelector('[data-st-coupon-best]');
    var applied = scope.querySelector('[data-st-coupon-applied]');
    var notice = scope.querySelector('[data-st-coupon-notice]');
    if (best) best.hidden = true;
    if (applied) applied.hidden = true;
    if (!notice) return;
    setText(notice, '[data-st-coupon-notice-code]', code || 'Coupon');
    notice.hidden = false;
  }

  function renderList(listEl, coupons, appliedCode) {
    var loading = listEl.querySelector('[data-st-coupon-list-loading]');
    if (loading) loading.remove();

    // Same split as the existing cart: Exclusive (disabled == false) + Other (disabled == true).
    var exclusive = coupons.filter(function (c) { return c.disabled == false; });
    var other = coupons.filter(function (c) { return c.disabled == true; });

    // Hide the hardcoded "Exclusive offers for you" heading when there are none.
    var heading = listEl.previousElementSibling;
    if (heading && heading.classList.contains('st-cart__coupon-list-title')) {
      heading.hidden = exclusive.length === 0;
    }

    if (!exclusive.length && !other.length) {
      listEl.innerHTML = '<p class="st-cart__coupon-empty">No coupons available right now.</p>';
      return;
    }

    var html = exclusive.map(function (c) { return couponRow(c, appliedCode); }).join('');
    if (other.length) {
      html += '<p class="st-cart__coupon-list-title">Other Coupons</p>';
      html += other.map(function (c) { return couponRow(c, appliedCode); }).join('');
    }
    listEl.innerHTML = html;
  }

  var COUPON_BADGE =
    '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M11.7819 0.671986C11.273 0.238239 10.6262 0 9.95756 0C9.28889 0 8.64209 0.238239 8.13318 0.671986L6.86943 1.74918C6.71932 1.87722 6.53265 1.95465 6.33598 1.97043L4.68129 2.10168C4.01475 2.15484 3.38895 2.44373 2.91614 2.91654C2.44333 3.38935 2.15444 4.01515 2.10128 4.68169L1.97003 6.33732C1.95398 6.5341 1.87622 6.72078 1.74784 6.87076L0.671587 8.13452C0.238089 8.64336 0 9.28997 0 9.95843C0 10.6269 0.238089 11.2735 0.671587 11.7823L1.74784 13.047C1.87588 13.1971 1.95331 13.3838 1.96909 13.5805L2.10128 15.2352C2.15444 15.9017 2.44333 16.5275 2.91614 17.0003C3.38895 17.4731 4.01475 17.762 4.68129 17.8152L6.33598 17.9464C6.53308 17.9623 6.72013 18.0401 6.87036 18.1686L8.13318 19.2449C8.64209 19.6786 9.28889 19.9169 9.95756 19.9169C10.6262 19.9169 11.273 19.6786 11.7819 19.2449L13.0457 18.1686C13.1961 18.0404 13.3831 17.963 13.5801 17.9474L15.2348 17.8152C15.9013 17.762 16.5271 17.4731 16.9999 17.0003C17.4727 16.5275 17.7616 15.9017 17.8148 15.2352L17.946 13.5805C17.9616 13.3835 18.0391 13.1965 18.1673 13.0461L19.2445 11.7833C19.6782 11.2744 19.9165 10.6276 19.9165 9.9589C19.9165 9.29023 19.6782 8.64343 19.2445 8.13452L18.1673 6.87076C18.0391 6.72041 17.9616 6.53338 17.946 6.33639L17.8148 4.68169C17.7616 4.01515 17.4727 3.38935 16.9999 2.91654C16.5271 2.44373 15.9013 2.15484 15.2348 2.10168L13.5791 1.97043C13.3825 1.95465 13.1958 1.87722 13.0457 1.74918L11.7819 0.671986ZM12.6088 5.98107L13.9354 7.3067L7.3063 13.9367L5.97973 12.6102L12.6088 5.98107ZM8.30006 8.30139C8.17033 8.43571 8.01516 8.54284 7.84359 8.61654C7.67202 8.69024 7.48749 8.72903 7.30077 8.73065C7.11405 8.73228 6.92887 8.6967 6.75605 8.62599C6.58323 8.55528 6.42622 8.45086 6.29418 8.31882C6.16214 8.18679 6.05772 8.02977 5.98702 7.85695C5.91631 7.68413 5.88073 7.49895 5.88235 7.31223C5.88397 7.12551 5.92277 6.94098 5.99647 6.76941C6.07017 6.59784 6.1773 6.44267 6.31161 6.31295C6.57683 6.05679 6.93206 5.91504 7.30077 5.91825C7.66949 5.92145 8.02219 6.06935 8.28293 6.33008C8.54366 6.59081 8.69155 6.94352 8.69476 7.31223C8.69796 7.68095 8.55622 8.03617 8.30006 8.30139ZM11.6151 13.6049C11.4785 13.4757 11.3692 13.3205 11.2936 13.1484C11.218 12.9763 11.1777 12.7908 11.1751 12.6029C11.1724 12.4149 11.2074 12.2284 11.2781 12.0542C11.3488 11.88 11.4537 11.7218 11.5866 11.5888C11.7194 11.4559 11.8776 11.351 12.0518 11.2802C12.2259 11.2094 12.4124 11.1743 12.6004 11.1769C12.7883 11.1794 12.9739 11.2197 13.146 11.2951C13.3181 11.3706 13.4734 11.4799 13.6026 11.6164C13.851 11.883 13.9862 12.2356 13.9798 12.5999C13.9734 12.9642 13.8258 13.3118 13.5681 13.5695C13.3105 13.8271 12.9629 13.9747 12.5986 13.9811C12.2342 13.9875 11.8816 13.8533 11.6151 13.6049Z" fill="url(#paint0_linear_109_10245)" />' +
      '<defs>' +
      '<linearGradient id="paint0_linear_109_10245" x1="9.95823" y1="19.9169" x2="9.95823" y2="-0.711317" gradientUnits="userSpaceOnUse">' +
      '<stop stop-color="#589D25" />' +
      '<stop offset="1" stop-color="#88EF3C" />' +
      '</linearGradient>' +
      '</defs>' +
    '</svg>';

  var APPLIED_TICK =
    '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M11.667 3.5L5.25 9.917 2.333 7" stroke="#1a8917" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  var CHEVRON =
    '<svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M5.74756 8.79736C5.31438 8.79733 4.89884 8.62491 4.59229 8.31885L0.281738 4.0083C0.166978 3.89334 0.101986 3.73714 0.102051 3.57471C0.102165 3.41221 0.16677 3.25595 0.281738 3.14111C0.396653 3.02627 0.552867 2.9624 0.715332 2.9624C0.877586 2.96245 1.03311 3.02651 1.14795 3.14111L5.45947 7.45264C5.53607 7.52909 5.64031 7.57178 5.74854 7.57178C5.85657 7.57169 5.96012 7.52892 6.03662 7.45264L10.3491 3.14014C10.4647 3.02851 10.6201 2.96686 10.7808 2.96826C10.9413 2.96978 11.095 3.03444 11.2085 3.14795C11.322 3.26155 11.3868 3.41513 11.3882 3.57568C11.3896 3.73635 11.327 3.89078 11.2153 4.00635L11.2144 4.0083L6.90283 8.31885C6.59625 8.62491 6.18077 8.79736 5.74756 8.79736Z" fill="#464A4F" stroke="#464A4F" stroke-width="0.204276" />' +
    '</svg>';

  function couponTerms(c) {
    var raw = c.termsAndConditions;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [String(raw)];
    } catch (e) {
      return [String(raw)];
    }
  }

  function couponRow(c, appliedCode) {
    var code = (c.couponCode || '').trim();
    var isDisabled = c.disabled == true;
    var isApplied = !!(appliedCode && code && code.toLowerCase() === String(appliedCode).toLowerCase());
    // Numeric savings for analytics (Coupon Selected From List): API discountValue, else the ₹
    // amount in saveText. Stored on the row so the click handler can read it.
    var saveMatch = String(c.saveText || '').match(/(\d[\d,]*)/);
    var saveVal = c.discountValue != null ? c.discountValue : (saveMatch ? saveMatch[1].replace(/,/g, '') : '');
    var desc = c.descriptionHeading || '';
    var terms = couponTerms(c);
    var hasMore = terms.length > 0;
    var termsHtml = hasMore
      ? '<ul class="st-cart__coupon-terms" data-st-coupon-terms hidden>' +
          terms.map(function (t) { return '<li>' + escapeHtml(t) + '</li>'; }).join('') +
        '</ul>'
      : '';
    return (
      '<div class="st-cart__coupon-item' + (isApplied ? ' is-applied' : '') + '" data-code="' + escapeHtml(code) + '" data-savings="' + escapeHtml(String(saveVal)) + '">' +
        '<div class="st-cart__coupon-item-top">' +
          '<div class="st-cart__coupon-item-lead">' +
            '<span class="st-cart__coupon-item-badge">' + COUPON_BADGE + '</span>' +
            '<div class="st-cart__coupon-item-text">' +
              '<span class="st-cart__coupon-item-code">' + escapeHtml(code) + '</span>' +
              '<p class="st-cart__coupon-item-save">' + (c.saveText || '') + '</p>' +
            '</div>' +
          '</div>' +
          (isApplied
            ? '<span class="st-cart__coupon-applied-pill">' + APPLIED_TICK + 'Applied</span>'
            : isDisabled
              ? '<button type="button" class="st-cart__coupon-apply-drawer" disabled>Apply</button>'
              : '<button type="button" class="st-cart__coupon-apply-drawer" data-st-coupon-apply-list>Apply</button>') +
        '</div>' +
        '<div class="st-cart__coupon-item-divider"></div>' +
        '<div class="st-cart__coupon-item-desc-wrap">' +
          (desc ? '<p class="st-cart__coupon-item-desc">' + escapeHtml(desc) + '</p>' : '') +
          (hasMore ? '<button type="button" class="st-cart__coupon-readmore" data-st-coupon-readmore>Read more ' + CHEVRON + '</button>' : '') +
          termsHtml +
        '</div>' +
      '</div>'
    );
  }

  /* ---- Main ------------------------------------------------------------- */
  function apply(scope, opts) {
    scope = scope || document;
    opts = opts || {};
    var inline = scope.querySelector('[data-st-coupon-inline]');
    if (!inline) return;

    var root = scope.querySelector('[data-st-content-root]');
    var email = root ? root.getAttribute('data-customer-email') : '';
    var overlay = scope.querySelector('[data-st-coupon-overlay]');
    var overlayCoupons = [];
    var applying = false; // single-flight lock: one coupon apply at a time (no stacking)

    var appliedCode = localStorage.getItem('discountCode');

    // Loader ↔ UI ↔ hidden state for the coupon card.
    var loaderEl = inline.querySelector('[data-st-coupon-loader]');
    var dividerEl = inline.querySelector('[data-st-coupon-divider]');
    var viewallEl = inline.querySelector('[data-st-coupon-viewall]');
    function showLoader() { if (loaderEl) loaderEl.hidden = false; if (dividerEl) dividerEl.hidden = true; if (viewallEl) viewallEl.hidden = true; }
    function showLoaded() { if (loaderEl) loaderEl.hidden = true; if (dividerEl) dividerEl.hidden = false; if (viewallEl) viewallEl.hidden = false; }
    function hideSection() { inline.hidden = true; }

    // A cart with only gift lines (no real products) has nothing coupons can apply to —
    // hide the card entirely (avoids a loader/"View all" card sitting over a gift-only cart).
    var offersData = readOffersData() || { items: [] };
    var hasRealItem = (offersData.items || []).some(function (i) {
      var t = i.tags || [];
      return t.indexOf('free_gift_new') < 0 && t.indexOf('surprise_gift') < 0 && t.indexOf('free_gift') < 0;
    });
    if (!hasRealItem) { hideSection(); return; }

    // Find the full coupon object (carries discountType / bxgy) for a code.
    function findCoupon(code) {
      var lc = (code || '').toLowerCase();
      return (overlayCoupons || []).filter(function (c) {
        return (c.couponCode || '').toLowerCase() === lc;
      })[0] || null;
    }

    // Disable every Apply button (best + manual + each list row) while a coupon is being
    // processed, so two coupons can't be applied at once. The clicked button is left to
    // the spinner state (setBtnLoading); the rest are greyed out.
    function setAllApplyDisabled(on, activeBtn) {
      var btns = [];
      var best = inline.querySelector('[data-st-coupon-apply-best]');
      if (best) btns.push(best);
      if (overlay) {
        var manual = overlay.querySelector('[data-st-coupon-apply-manual]');
        if (manual) btns.push(manual);
        Array.prototype.push.apply(btns, overlay.querySelectorAll('[data-st-coupon-apply-list]'));
      }
      btns.forEach(function (b) {
        if (b === activeBtn) return;
        b.disabled = on;
        b.classList.toggle('is-disabled', on);
      });
      // Lock the rest of the overlay during the round-trip too: the code input + the back/close
      // button, so nothing else can be touched while a coupon is being applied.
      if (overlay) {
        var inp = overlay.querySelector('[data-st-coupon-input]');
        if (inp) inp.disabled = on;
        var closeBtn = overlay.querySelector('[data-st-coupon-overlay-close]');
        if (closeBtn) { closeBtn.disabled = on; closeBtn.classList.toggle('is-disabled', on); }
      }
    }

    // `btn` is the clicked Apply button (best / list row / manual input). We show an
    // in-button spinner for the whole round-trip + disable all OTHER Apply buttons (single-
    // flight, so coupons can't stack). On success the drawer re-renders via onChange (the
    // buttons are replaced, so the lock clears itself); on any failure we unlock + error.
    function doApply(code, btn, from) {
      if (!code || applying) return;
      from = from || 'inline_best_coupon';
      applying = true;
      setAllApplyDisabled(true, btn);
      setBtnLoading(btn, true);
      var unlock = function () {
        applying = false;
        setBtnLoading(btn, false);
        setAllApplyDisabled(false, btn);
      };
      getCart().then(function (cart) {
        return applyCoupon(code, cart, email).then(function (result) {
          if (!isApplicable(result)) {
            unlock();
            couponToast('This coupon is not valid. Please try another.');
            if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.couponDenied === 'function') {
              window.StickyCartAnalytics.couponDenied(code, denyReason(result), from);
            }
            return;
          }
          var discountPaise = deriveDiscountPaise(result, cart);
          localStorage.setItem('discountCode', code);
          localStorage.setItem('handle-cart', JSON.stringify(result));
          if (discountPaise != null) {
            localStorage.setItem('disc_price', String(Math.round(discountPaise / 100)));
          }
          appliedInSession = true;
          if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.couponApplied === 'function') {
            window.StickyCartAnalytics.couponApplied(code, discountPaise != null ? Math.round(discountPaise / 100) : null, 'Sticky Cart');
          }
          if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.couponApplied2 === 'function') {
            window.StickyCartAnalytics.couponApplied2(code, discountPaise != null ? Math.round(discountPaise / 100) : null, from);
          }
          if (overlay) overlay.hidden = true;

          // Post-apply cart mutations (parity with the existing cart), then refresh once:
          //  • drop any line the backend flagged hide_from_frontend
          //  • swap out a stale bxgy gift, and add this coupon's bxgy gift (if any)
          var coupon = findCoupon(code);
          var prevBxgy = readBxgy();
          var tasks = [removeHiddenItems(result)];
          if (prevBxgy && prevBxgy.discountCodeBxgy !== code) tasks.push(removeStoredBxgy());
          if (coupon && coupon.discountType === 'bxgy' && coupon.bxgy) tasks.push(applyBxgy(code, coupon));
          return Promise.all(tasks).then(function () {
            if (typeof opts.onChange === 'function') opts.onChange();
          });
        });
      }).catch(function (err) {
        if (window.console) console.error('[StickyCart] doApply', err);
        unlock();
        couponToast('Something went wrong. Please try again.');
      });
    }

    function doRemove() {
      var removed = localStorage.getItem('discountCode');
      var lostStr = localStorage.getItem('disc_price');
      var lost = lostStr ? parseInt(lostStr, 10) || 0 : 0;
      localStorage.removeItem('discountCode');
      localStorage.removeItem('handle-cart');
      localStorage.removeItem('disc_price');
      if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.couponRemoved === 'function') {
        window.StickyCartAnalytics.couponRemoved(removed);
      }
      if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.couponRemoved2 === 'function') {
        window.StickyCartAnalytics.couponRemoved2(removed, lost);
      }
      // Remove the bxgy gift that was added with this coupon, then refresh.
      removeStoredBxgy().then(function () {
        if (typeof opts.onChange === 'function') opts.onChange();
      });
    }

    // Applied state: reflect stored discount on the freshly rendered bill.
    if (appliedCode) {
      showLoaded(); // applied state comes from storage — no API wait, reveal immediately
      var stored = null;
      try { stored = JSON.parse(localStorage.getItem('handle-cart')); } catch (e) {}
      var discountPaise = null;
      var discStr = localStorage.getItem('disc_price');
      if (discStr != null && discStr !== '') discountPaise = parseInt(discStr, 10) * 100;
      if (discountPaise == null && stored) {
        getCart().then(function (cart) {
          discountPaise = deriveDiscountPaise(stored, cart);
          overrideTotals(scope, discountPaise);
          renderApplied(inline, appliedCode, discountPaise);
        });
      } else {
        overrideTotals(scope, discountPaise);
        renderApplied(inline, appliedCode, discountPaise);
      }
      bindRemove(inline, doRemove);
      bindViewAll(inline, overlay, function () { return overlayCoupons; });
      // Even with a coupon already applied, fetch the offers so the "View all Coupons"
      // overlay is populated. Previously this branch returned early without calling
      // fetchOffers/primeOverlay, so opening the overlay showed an endless spinner.
      fetchOffers().then(function (coupons) {
        overlayCoupons = coupons || [];
        if (overlay) primeOverlay(overlay, overlayCoupons, doApply);
      });
      return;
    }

    // No coupon applied: surface the best coupon + prime the overlay list.
    // Offers payload is built entirely from the server-rendered [data-st-offers-items]
    // (re-rendered on every cart mutation = the last updated cart). Show a loader until
    // the offers API resolves; reveal the UI on success, hide the whole card on failure
    // or when there are no coupons (fetchOffers returns [] on error).
    showLoader();
    fetchOffers().then(function (coupons) {
      overlayCoupons = coupons || [];
      // A coupon was just auto-invalidated (reconcile) → show the notice instead of the best
      // suggestion, keeping the "View all Coupons" link. One-shot: consumed on display.
      var invalidCode = null;
      try { invalidCode = sessionStorage.getItem('stCouponInvalidated'); sessionStorage.removeItem('stCouponInvalidated'); } catch (e) {}
      if (invalidCode) {
        showLoaded();
        renderNotice(inline, invalidCode);
        if (overlay) primeOverlay(overlay, overlayCoupons, doApply);
        bindViewAll(inline, overlay, function () { return overlayCoupons; });
        return;
      }
      if (!overlayCoupons.length) {
        // API failed or no coupons available → hide the coupon card entirely.
        hideSection();
        return;
      }
      showLoaded();
      var best = overlayCoupons.filter(function (c) { return c.isBestCoupon && !c.disabled; })[0] ||
        overlayCoupons.filter(function (c) { return !c.disabled; })[0];
      if (best) {
        renderBest(inline, best);
        var applyBest = inline.querySelector('[data-st-coupon-apply-best]');
        if (applyBest) {
          applyBest.addEventListener('click', function () {
            doApply((best.couponCode || '').trim(), applyBest, 'inline_best_coupon');
          });
        }
      }
      if (overlay) primeOverlay(overlay, overlayCoupons, doApply);
      bindViewAll(inline, overlay, function () { return overlayCoupons; });
    });
  }

  function primeOverlay(overlay, coupons, doApply) {
    var listEl = overlay.querySelector('[data-st-coupon-list]');
    // Reflect a coupon already applied (on the cart screen) as "Applied" in the drawer list.
    if (listEl) renderList(listEl, coupons, localStorage.getItem('discountCode') || '');

    var input = overlay.querySelector('[data-st-coupon-input]');
    var manual = overlay.querySelector('[data-st-coupon-apply-manual]');
    if (manual && input) {
      // Apply is active (orange + pointer) only while the input holds a code; empty → inactive.
      var syncManualActive = function () { manual.classList.toggle('is-active', !!(input.value || '').trim()); };
      input.addEventListener('input', syncManualActive);
      syncManualActive();
      var submitManual = function () {
        var code = (input.value || '').trim().toUpperCase();
        if (!code) return;
        if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.couponCodeEntered === 'function') {
          window.StickyCartAnalytics.couponCodeEntered(code);
        }
        doApply(code, manual, 'manual_entry');
      };
      manual.addEventListener('click', submitManual);
      // Pressing Enter in the field applies immediately (same as clicking Apply).
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.keyCode === 13) { e.preventDefault(); submitManual(); }
      });
    }
    if (listEl) {
      listEl.addEventListener('click', function (e) {
        var readMore = e.target.closest('[data-st-coupon-readmore]');
        if (readMore) {
          var item = readMore.closest('.st-cart__coupon-item');
          var termsEl = item && item.querySelector('[data-st-coupon-terms]');
          if (termsEl) {
            var willOpen = termsEl.hidden;
            termsEl.hidden = !willOpen;
            readMore.classList.toggle('is-open', willOpen);
            readMore.firstChild.nodeValue = willOpen ? 'Read less ' : 'Read more ';
          }
          if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.couponReadMore === 'function') {
            var ri = readMore.closest('[data-code]');
            window.StickyCartAnalytics.couponReadMore(ri && ri.getAttribute('data-code'), indexOfRow(listEl, ri));
          }
          return;
        }
        var btn = e.target.closest('[data-st-coupon-apply-list]');
        if (!btn) return;
        var row = btn.closest('[data-code]');
        if (row) {
          if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.couponSelectedFromList === 'function') {
            var sv = row.getAttribute('data-savings');
            window.StickyCartAnalytics.couponSelectedFromList(row.getAttribute('data-code'), sv ? parseInt(sv, 10) : null, btn.disabled ? 'disabled' : 'enabled', indexOfRow(listEl, row));
          }
          doApply(row.getAttribute('data-code'), btn, 'coupon_list');
        }
      });
    }
    var closeBtn = overlay.querySelector('[data-st-coupon-overlay-close]');
    if (closeBtn) closeBtn.addEventListener('click', function () {
      overlay.hidden = true;
      if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.couponListClosed === 'function') {
        window.StickyCartAnalytics.couponListClosed(appliedInSession, listOpenedAt ? Date.now() - listOpenedAt : 0);
      }
    });
  }

  // 0-based index of a coupon row among its siblings (for analytics coupon_index).
  function indexOfRow(listEl, row) {
    if (!row) return null;
    var rows = listEl.querySelectorAll('[data-code]');
    for (var i = 0; i < rows.length; i++) if (rows[i] === row) return i;
    return null;
  }

  function bindViewAll(inline, overlay, getCoupons) {
    var viewAll = inline.querySelector('[data-st-coupon-viewall]');
    if (viewAll && overlay) {
      viewAll.addEventListener('click', function () {
        overlay.hidden = false;
        appliedInSession = false;
        listOpenedAt = Date.now();
        var list = typeof getCoupons === 'function' ? getCoupons() : [];
        if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.couponListViewed === 'function') {
          window.StickyCartAnalytics.couponListViewed(list);
        }
        if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.couponListOpened === 'function') {
          window.StickyCartAnalytics.couponListOpened(list);
        }
      });
    }
  }

  function bindRemove(inline, doRemove) {
    var removeBtn = inline.querySelector('[data-st-coupon-remove]');
    if (removeBtn) removeBtn.addEventListener('click', doRemove);
  }

  function setText(scope, sel, val) {
    var el = scope.querySelector(sel);
    if (el) el.textContent = val;
  }

  // Toggle the in-button spinner on an Apply button (best / list / manual) while its
  // coupon request is in flight. CSS (.is-loading) hides the label + draws the spinner.
  function setBtnLoading(btn, on) {
    if (!btn) return;
    btn.classList.toggle('is-loading', !!on);
    btn.disabled = !!on;
    if (on) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
  }

  // Save copy from the offers API contains markup (e.g. "Save <b>₹250</b> …") —
  // render it as HTML so the <b> bolds instead of showing literally.
  function setHtml(scope, sel, val) {
    var el = scope.querySelector(sel);
    if (el) el.innerHTML = val == null ? '' : val;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* ---- Reconcile against handle-cart on every render --------------------
     Parity with the existing cart page, which re-validates the cart against
     /cart/handle-cart on every load (= after every mutation): cart-script.liquid
     :437-485 (no-coupon) + cart-revamp callNewCoupon (coupon). Runs with or without
     a coupon; signature-gated + single-flight so it fires once per distinct cart
     state and converges (a removal/clear → re-render → reconcile → settles). */
  var reconcileInFlight = false;
  var reconcilePending = false;   // a render arrived mid-flight → re-run after current finishes
  var lastReconcileScope = null;
  var lastReconcileOpts = null;
  var lastReconcileSig = null;

  function cartSig(cart, code) {
    return (cart.items || [])
      .map(function (i) { return i.key + ':' + i.quantity; })
      .sort()
      .join('|') + '|' + (code || '');
  }

  function reconcile(scope, opts) {
    scope = scope || document;
    opts = opts || {};
    lastReconcileScope = scope;
    lastReconcileOpts = opts;
    // Mid-flight: coalesce instead of dropping, so the FINAL cart state (after rapid
    // mutations) is always re-validated rather than silently skipped.
    if (reconcileInFlight) { reconcilePending = true; return; }
    // Mark in-flight SYNCHRONOUSLY (before the async getCart) so isReconciling() is true the
    // instant a reconcile is scheduled. Otherwise there's a gap right after a cart update where
    // the flag isn't set yet — a fast /order/fees makes pricing look "settled" and lets Proceed
    // slip through with a coupon about to be removed. The settle .then always clears it.
    reconcileInFlight = true;
    var root = scope.querySelector('[data-st-content-root]');
    var email = root ? root.getAttribute('data-customer-email') : '';

    getCart().then(function (cart) {
      if (!cart || !(cart.items || []).length) { lastReconcileSig = null; return; }
      var code = localStorage.getItem('discountCode') || '';
      var sig = cartSig(cart, code);
      if (sig === lastReconcileSig) return; // this exact cart/coupon state is already reconciled

      // applyCoupon posts the SAME handle-cart body the cart page uses (bare variant ids,
      // in-stock lines, coupon = applied code or ''); reused here for reconciliation.
      return applyCoupon(code, cart, email).then(function (result) {
        // Persist fresh handle-cart + cartId (cart-script.liquid:459-462).
        try { localStorage.setItem('handle-cart', JSON.stringify(result)); } catch (e) {}
        var newId = result && result.id ? bareId(result.id) : null;
        if (newId) { try { localStorage.setItem('cartIdd', newId); } catch (e) {} }

        var nodes = result && result.checkoutDiscountCodeApplyV2 &&
          result.checkoutDiscountCodeApplyV2.checkout &&
          result.checkoutDiscountCodeApplyV2.checkout.lineItems &&
          result.checkoutDiscountCodeApplyV2.checkout.lineItems.nodes;
        var hadHidden = !!(nodes && nodes.some(function (n) { return n && n.hide_from_frontend === true; }));

        return removeHiddenItems(result).then(function () {
          if (hadHidden) {
            // Cart changed → re-render; the next pass reconciles the trimmed cart.
            lastReconcileSig = null;
            if (typeof opts.onChange === 'function') opts.onChange();
            return;
          }

          if (code) {
            if (!isApplicable(result)) {
              // Coupon no longer valid for this cart — clear it (+ any bxgy) like the cart page.
              localStorage.removeItem('discountCode');
              localStorage.removeItem('handle-cart');
              localStorage.removeItem('disc_price');
              if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.couponRemoved === 'function') {
                window.StickyCartAnalytics.couponRemoved(code);
              }
              lastReconcileSig = null;
              // One-shot flag so the next render shows the "<code> is not valid anymore" notice
              // (survives the section refresh + the multipass login reload).
              try { sessionStorage.setItem('stCouponInvalidated', code); } catch (e) {}
              return removeStoredBxgy().then(function () {
                if (typeof opts.onChange === 'function') opts.onChange();
              });
            }
            // Still applicable → recompute the discount for the new cart.
            var discountPaise = deriveDiscountPaise(result, cart);
            var prev = localStorage.getItem('disc_price');
            var next = discountPaise != null ? String(Math.round(discountPaise / 100)) : prev;
            if (discountPaise != null) localStorage.setItem('disc_price', next);
            lastReconcileSig = sig;
            if (prev !== next) {
              // Update the bill coupon row AND the applied-coupon "Saved ₹X" text (so they
              // never disagree), then let fees recompute the total (no full refetch).
              overrideTotals(scope, discountPaise);
              renderApplied(scope, code, discountPaise);
              if (window.StickyCartFees && typeof window.StickyCartFees.apply === 'function') {
                window.StickyCartFees.apply(scope);
              }
            }
            return;
          }

          // No coupon: handle-cart only refreshed cartId / removed hidden items.
          lastReconcileSig = sig;
        });
      });
    }).catch(function (err) {
      if (window.console) console.error('[StickyCart] reconcile', err);
    }).then(function () {
      // Settle runs on EVERY getCart() path — including the early returns inside the callback
      // (empty cart, or `sig === lastReconcileSig` = an unchanged cart re-render, the common
      // case). Previously the settle was chained on applyCoupon INSIDE the getCart callback, so
      // those early returns never cleared reconcileInFlight → isReconciling() got stuck true →
      // pricingSettled() was always false → every Proceed waited the full 4s timeout. Root cause.
      reconcileInFlight = false;
      try { document.dispatchEvent(new Event('stcart:pricing-updated')); } catch (e) {}
      // A render arrived while busy — re-reconcile the latest state. The
      // sig === lastReconcileSig guard makes this a no-op once settled (no loop).
      if (reconcilePending) { reconcilePending = false; reconcile(lastReconcileScope, lastReconcileOpts); }
    });
  }

  // True while a coupon re-validation round-trip is in flight (or queued) — the controller
  // checks this before advancing to Review so the price is never shown mid-update.
  function isReconciling() { return reconcileInFlight || reconcilePending; }

  window.StickyCartCoupon = { apply: apply, reconcile: reconcile, isReconciling: isReconciling };
})();
